import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  listPatients,
  createPatient,
  deactivatePatient,
} from "../../services/GameService";
import "./PatientManagement.css";

const emptyForm = {
  username: "",
  email: "",
  password: "",
  first_name: "",
  last_name: "",
  date_of_birth: "",
  notes: "",
};

const FIELDS = [
  { name: "username", label: "Username", type: "text", required: true },
  { name: "email", label: "Email", type: "email", required: true },
  { name: "password", label: "Temporary password", type: "password", required: true },
  { name: "first_name", label: "First name", type: "text", required: true },
  { name: "last_name", label: "Last name", type: "text", required: false },
  { name: "date_of_birth", label: "Date of birth", type: "date", required: true },
];

// Admin Mode: a clinician's roster of patients with create / deactivate / drill-in.
const PatientManagement = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setPatients(await listPatients());
    } catch (e) {
      setError("Could not load patients.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setSubmitting(true);
    try {
      await createPatient(form);
      setForm(emptyForm);
      setShowForm(false);
      await load();
    } catch (err) {
      const data = err.response?.data;
      setFormError(
        data
          ? Object.entries(data)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(" ") : v}`)
              .join("  ")
          : "Could not create patient."
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeactivate = async (id) => {
    try {
      await deactivatePatient(id);
      await load();
    } catch (e) {
      setError("Could not deactivate patient.");
    }
  };

  return (
    <div className="patient-management">
      <div className="pm-header">
        <h1>My Patients</h1>
        <button
          className="pm-btn pm-btn-primary"
          onClick={() => setShowForm((s) => !s)}
        >
          {showForm ? "Cancel" : "Add patient"}
        </button>
      </div>

      {showForm && (
        <form className="pm-form" onSubmit={handleCreate}>
          {FIELDS.map((field) => (
            <label key={field.name} className="pm-field">
              <span>{field.label}</span>
              <input
                name={field.name}
                type={field.type}
                value={form[field.name]}
                onChange={handleChange}
                required={field.required}
              />
            </label>
          ))}
          <label className="pm-field pm-field-wide">
            <span>Notes</span>
            <textarea name="notes" value={form.notes} onChange={handleChange} />
          </label>
          {formError && (
            <p className="pm-error" role="alert">
              {formError}
            </p>
          )}
          <button
            type="submit"
            className="pm-btn pm-btn-primary"
            disabled={submitting}
          >
            {submitting ? "Creating…" : "Create patient"}
          </button>
        </form>
      )}

      {error && (
        <p className="pm-error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <p>Loading…</p>
      ) : patients.length === 0 ? (
        <p className="pm-empty">No patients yet. Add your first patient above.</p>
      ) : (
        <table className="pm-table">
          <thead>
            <tr>
              <th>Username</th>
              <th>Name</th>
              <th>Email</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {patients.map((p) => (
              <tr key={p.id}>
                <td>{p.username}</td>
                <td>
                  {p.first_name} {p.last_name}
                </td>
                <td>{p.email}</td>
                <td className="pm-actions">
                  <Link to={`/admin/patients/${p.id}`} className="pm-link">
                    Details
                  </Link>
                  <button
                    className="pm-btn pm-btn-danger"
                    onClick={() => handleDeactivate(p.id)}
                  >
                    Deactivate
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
};

export default PatientManagement;
